/*
 * Palo Alto Theme
 *
 * Binaery ATC upsell popup
 */

(function () {
  "use strict";

  const SELECTORS = {
    config: "[data-bi-upsell-config]",
    modal: "[data-bi-upsell-modal]",
    atcButton: "[data-add-to-cart]",
    quickViewOnboarding: "[data-quick-view-onboarding]",
  };

  const productCache = new Map();
  let cartCache = null;
  let cartCacheTime = 0;
  let activePopup = null;
  let activeContext = null;

  function getConfigs() {
    const nodes = document.querySelectorAll(SELECTORS.config);
    const configs = [];

    for (const node of nodes) {
      try {
        configs.push(JSON.parse(node.textContent));
      } catch (error) {
        console.warn("bi-upsell-popup: invalid config", error);
      }
    }

    return configs;
  }

  function getMoneyFormat() {
    const root = document.querySelector(".bi-upsell-popup");
    const format = root && root.dataset.moneyFormat;

    if (format) {
      return format;
    }

    if (window.theme && window.theme.moneyFormat) {
      return window.theme.moneyFormat;
    }

    return "${{amount}}";
  }

  function getRoundedMoneyFormat() {
    const format = getMoneyFormat();

    if (format.indexOf("amount_no_decimals") !== -1) {
      return format;
    }

    if (format.indexOf("amount_with_comma_separator") !== -1) {
      return format.replace(
        /\{\{\s*amount_with_comma_separator\s*\}\}/,
        "{{amount_no_decimals_with_comma_separator}}",
      );
    }

    if (format.indexOf("amount_with_apostrophe_separator") !== -1) {
      return format.replace(
        /\{\{\s*amount_with_apostrophe_separator\s*\}\}/,
        "{{amount_no_decimals_with_apostrophe_separator}}",
      );
    }

    if (format.indexOf("amount_with_space_separator") !== -1) {
      return format.replace(
        /\{\{\s*amount_with_space_separator\s*\}\}/,
        "{{amount_no_decimals_with_space_separator}}",
      );
    }

    if (format.indexOf("amount_with_period_and_space_separator") !== -1) {
      return format.replace(
        /\{\{\s*amount_with_period_and_space_separator\s*\}\}/,
        "{{amount_no_decimals_with_period_and_space_separator}}",
      );
    }

    return format.replace(/\{\{\s*amount\s*\}\}/, "{{amount_no_decimals}}");
  }

  function formatPrice(cents) {
    const roundedCents = Math.round(cents / 100) * 100;

    if (window.theme && typeof window.theme.formatMoney === "function") {
      return window.theme.formatMoney(roundedCents, getRoundedMoneyFormat());
    }

    return "$" + Math.round(roundedCents / 100);
  }

  function replacePriceToken(text, cents) {
    if (!text) {
      return "";
    }

    return text.split("{price}").join(formatPrice(cents));
  }

  function productIsTrigger(product, triggerProducts) {
    if (
      !product ||
      !Array.isArray(triggerProducts) ||
      !triggerProducts.length
    ) {
      return false;
    }

    const productId = product.id;

    for (const triggerProduct of triggerProducts) {
      if (triggerProduct && triggerProduct.id === productId) {
        return true;
      }
    }

    return false;
  }

  function getHandleFromPath(path) {
    const match = String(path || "").match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  function resolveProductHandle(button, form) {
    const handleNode = button.closest("[data-handle], [data-swatch-handle]");

    if (handleNode) {
      if (handleNode.dataset.handle) {
        return handleNode.dataset.handle;
      }

      if (handleNode.dataset.swatchHandle) {
        return handleNode.dataset.swatchHandle;
      }
    }

    const productRoot = button.closest(
      ".product-grid-item, [data-product], .bi-ani__slide, .wis-diff__product",
    );

    if (productRoot) {
      const productLink = productRoot.querySelector('a[href*="/products/"]');

      if (productLink) {
        const handle = getHandleFromPath(
          productLink.pathname || productLink.getAttribute("href"),
        );
        if (handle) {
          return handle;
        }
      }
    }

    if (form && form.action) {
      const handle = getHandleFromPath(form.action);
      if (handle) {
        return handle;
      }
    }

    return getHandleFromPath(window.location.pathname);
  }

  function getVariantIdFromForm(form) {
    if (!form) {
      return null;
    }

    const idInput = form.querySelector('[name="id"]');

    if (!idInput) {
      return null;
    }

    const value = parseInt(idInput.value, 10);
    return isNaN(value) ? null : value;
  }

  async function fetchProduct(handle) {
    if (!handle) {
      return null;
    }

    if (productCache.has(handle)) {
      return productCache.get(handle);
    }

    const response = await fetch(
      window.theme.routes.root + "products/" + handle + ".js",
    );

    if (!response.ok) {
      return null;
    }

    const product = await response.json();
    productCache.set(handle, product);
    return product;
  }

  async function fetchCart(forceRefresh) {
    const now = Date.now();

    if (!forceRefresh && cartCache && now - cartCacheTime < 5000) {
      return cartCache;
    }

    const response = await fetch(window.theme.routes.cart_url + ".js");

    if (!response.ok) {
      return { items: [] };
    }

    cartCache = await response.json();
    cartCacheTime = now;
    return cartCache;
  }

  function cartHasAdvancedProduct(cart, advancedProductId) {
    if (!cart || !cart.items || !cart.items.length) {
      return false;
    }

    for (const item of cart.items) {
      if (item.product_id === advancedProductId) {
        return true;
      }
    }

    return false;
  }

  function readCookie(name) {
    if (!document.cookie) {
      return null;
    }

    const cookies = document.cookie.split("; ");

    for (const cookie of cookies) {
      if (cookie.indexOf(name + "=") === 0) {
        return cookie.substring(name.length + 1);
      }
    }

    return null;
  }

  function writeCookie(name, value, days) {
    const config = {
      path: "/",
      domain: window.location.hostname,
      sameSite: "none",
      secure: true,
    };

    let expires = "";

    if (days > 0) {
      const date = new Date();
      date.setTime(date.getTime() + 3600000 * 24 * days);
      expires = "; expires=" + date.toUTCString();
    }

    document.cookie =
      name +
      "=" +
      value +
      expires +
      "; path=" +
      config.path +
      "; domain=" +
      config.domain +
      "; sameSite=" +
      config.sameSite +
      "; secure=" +
      config.secure;
  }

  function shouldSuppressPopup(config) {
    if (
      config.frequency.oncePerVisit &&
      sessionStorage.getItem(config.frequency.sessionKey) === "1"
    ) {
      return true;
    }

    const daysBetween = parseInt(config.frequency.daysBetween, 10) || 0;

    if (daysBetween <= 0) {
      return false;
    }

    const cookieValue = readCookie(config.frequency.cookieName);

    if (!cookieValue) {
      return false;
    }

    const lastShown = parseInt(cookieValue, 10);

    if (isNaN(lastShown)) {
      return false;
    }

    const elapsedMs = Date.now() - lastShown;
    const intervalMs = daysBetween * 24 * 60 * 60 * 1000;

    return elapsedMs < intervalMs;
  }

  function markPopupShown(config) {
    if (config.frequency.oncePerVisit) {
      sessionStorage.setItem(config.frequency.sessionKey, "1");
    }

    const daysBetween = parseInt(config.frequency.daysBetween, 10) || 0;

    if (daysBetween > 0) {
      writeCookie(config.frequency.cookieName, String(Date.now()), daysBetween);
    }
  }

  function findMatchingConfig(configs, product) {
    for (const config of configs) {
      if (!config.enabled) {
        continue;
      }

      if (!config.triggerProducts || !config.triggerProducts.length) {
        continue;
      }

      if (productIsTrigger(product, config.triggerProducts)) {
        return config;
      }
    }

    return null;
  }

  function getVariantPrice(product, variantId) {
    if (!product || !product.variants) {
      return 0;
    }

    for (const variant of product.variants) {
      if (variant.id === variantId) {
        return variant.price;
      }
    }

    if (product.variants.length) {
      return product.variants[0].price;
    }

    return 0;
  }

  function getConfigByBlockId(blockId) {
    const modal = document.querySelector(
      '[data-bi-upsell-modal="' + blockId + '"]',
    );

    if (!modal) {
      return null;
    }

    const configNode = modal.previousElementSibling;

    if (!configNode || !configNode.matches("[data-bi-upsell-config]")) {
      return null;
    }

    try {
      return JSON.parse(configNode.textContent);
    } catch (error) {
      return null;
    }
  }

  function isEditorPreview(modal) {
    return modal && modal.dataset.editorPreview === "true";
  }

  function closeAllEditorPreviews() {
    const modals = document.querySelectorAll(
      '[data-bi-upsell-modal][data-editor-preview="true"]',
    );

    for (const modal of modals) {
      closeEditorPreview(modal);
    }
  }

  function closeEditorPreview(modal) {
    if (!modal) {
      return;
    }

    modal.dataset.editorPreview = "false";
    closePopup(modal);
  }

  function openEditorPreview(blockId) {
    const modal = document.querySelector(
      '[data-bi-upsell-modal="' + blockId + '"]',
    );

    if (!modal) {
      return;
    }

    const config = getConfigByBlockId(blockId);
    const previewPrice =
      config && config.previewPrice
        ? config.previewPrice
        : parseInt(modal.dataset.previewPrice, 10) || 2200;

    closeAllEditorPreviews();
    bindModalEvents(modal);

    if (config) {
      updatePopupContent(modal, config, getPreviewDisplayPrice(config, modal));
    }

    modal.dataset.editorPreview = "true";
    openPopup(modal);
  }

  function handleEditorBlockSelect(event) {
    if (!event.detail || !event.detail.blockId) {
      return;
    }

    const modal = document.querySelector(
      '[data-bi-upsell-modal="' + event.detail.blockId + '"]',
    );

    if (!modal) {
      return;
    }

    openEditorPreview(event.detail.blockId);
  }

  function handleEditorBlockDeselect(event) {
    if (!event.detail || !event.detail.blockId) {
      return;
    }

    const modal = document.querySelector(
      '[data-bi-upsell-modal="' + event.detail.blockId + '"]',
    );

    if (modal && isEditorPreview(modal)) {
      closeEditorPreview(modal);
    }
  }

  function handleEditorSectionDeselect(event) {
    if (!event.target || !event.target.querySelector(".bi-upsell-popup")) {
      return;
    }

    closeAllEditorPreviews();
  }

  function handleEditorSectionLoad(event) {
    if (!event.target || !event.target.querySelector(".bi-upsell-popup")) {
      return;
    }

    const configs = getConfigs();

    for (const config of configs) {
      const modal = document.querySelector(
        '[data-bi-upsell-modal="' + config.id + '"]',
      );

      if (modal) {
        modal.dataset.bound = "false";
        bindModalEvents(modal);
      }
    }

    if (event.detail && event.detail.blockId) {
      openEditorPreview(event.detail.blockId);
    }
  }

  function initThemeEditor() {
    if (!window.Shopify || !window.Shopify.designMode) {
      return;
    }

    document.addEventListener("shopify:block:select", handleEditorBlockSelect);
    document.addEventListener(
      "shopify:block:deselect",
      handleEditorBlockDeselect,
    );
    document.addEventListener(
      "shopify:section:deselect",
      handleEditorSectionDeselect,
    );
    document.addEventListener("shopify:section:load", handleEditorSectionLoad);
  }

  function getDiscountedAdvancedPrice(advancedPriceCents, discount) {
    if (!discount || !discount.enabled || !advancedPriceCents) {
      return advancedPriceCents;
    }

    if (discount.type === "fixed") {
      const amount = parseInt(discount.amount, 10) || 0;

      if (amount > 0) {
        return Math.max(0, advancedPriceCents - amount);
      }

      return advancedPriceCents;
    }

    const percent = parseInt(discount.percent, 10) || 0;

    if (percent > 0) {
      return Math.max(
        0,
        advancedPriceCents - Math.round((advancedPriceCents * percent) / 100),
      );
    }

    return advancedPriceCents;
  }

  function getDisplayPriceDiff(advancedPriceCents, clickedPriceCents, config) {
    const advancedPrice = parseInt(advancedPriceCents, 10) || 0;
    const clickedPrice = parseInt(clickedPriceCents, 10) || 0;
    const discountedAdvanced = getDiscountedAdvancedPrice(
      advancedPrice,
      config && config.discount,
    );

    return Math.max(0, discountedAdvanced - clickedPrice);
  }

  function getPreviewDisplayPrice(config, modal) {
    const previewDiff =
      config && config.previewPrice
        ? config.previewPrice
        : parseInt(modal.dataset.previewPrice, 10) || 2200;
    const advancedPrice =
      config && config.advanced && config.advanced.price
        ? config.advanced.price
        : 0;

    if (!advancedPrice) {
      return previewDiff;
    }

    const clickedPrice = Math.max(0, advancedPrice - previewDiff);

    return getDisplayPriceDiff(advancedPrice, clickedPrice, config);
  }

  function updatePopupContent(modal, config, priceDiff) {
    const title = modal.querySelector("[data-bi-upsell-title]");
    const subtitle = modal.querySelector("[data-bi-upsell-subtitle]");
    const features = modal.querySelector("[data-bi-upsell-features]");
    const acceptText = modal.querySelector("[data-bi-upsell-accept-text]");

    if (title && config.content.title) {
      title.textContent = replacePriceToken(config.content.title, priceDiff);
    }

    if (subtitle && config.content.subtitle) {
      subtitle.innerHTML = replacePriceToken(
        config.content.subtitle,
        priceDiff,
      );
    }

    if (features && config.content.features) {
      features.innerHTML = replacePriceToken(
        config.content.features,
        priceDiff,
      );
    }

    if (acceptText && config.content.acceptText) {
      acceptText.textContent = replacePriceToken(
        config.content.acceptText,
        priceDiff,
      );
    }
  }

  function trackUpsellEvent(eventName, context, action) {
    if (!context || !context.config || context.config.preview) {
      return;
    }

    if (context.modal && isEditorPreview(context.modal)) {
      return;
    }

    const config = context.config;
    const advanced = config.advanced || {};
    const payload = {
      event: eventName,
      upsell_id: config.id,
      upsell_action: action || "",
      upsell_product_handle: advanced.handle || "",
      upsell_product_id: advanced.productId || "",
      upsell_variant_id: advanced.variantId || "",
      trigger_product_handle: context.triggerHandle || "",
      trigger_product_id: context.triggerProductId || "",
      upsell_price_diff: context.priceDiff || 0,
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  function openPopup(modal) {
    modal.classList.add("bi-upsell-popup__modal--visible");
    modal.setAttribute("aria-hidden", "false");
    document.body.classList.add("bi-upsell-popup-open");
    activePopup = modal;

    const focusTarget =
      modal.querySelector("[data-bi-upsell-accept]") ||
      modal.querySelector("[data-bi-upsell-close]");

    if (focusTarget) {
      focusTarget.focus();
    }
  }

  function releaseModalFocus(modal, restoreFocusTo) {
    if (restoreFocusTo && typeof restoreFocusTo.focus === "function") {
      restoreFocusTo.focus();
      return;
    }

    const activeElement = document.activeElement;

    if (activeElement && modal.contains(activeElement)) {
      activeElement.blur();
    }
  }

  function closePopup(modal, restoreFocusTo) {
    releaseModalFocus(modal, restoreFocusTo);
    modal.classList.remove("bi-upsell-popup__modal--visible");
    modal.setAttribute("aria-hidden", "true");
    document.body.classList.remove("bi-upsell-popup-open");

    if (activePopup === modal) {
      activePopup = null;
      activeContext = null;
    }
  }

  function buildAdvancedFormData(config, quantity) {
    const formData = new FormData();
    formData.append("id", config.advanced.variantId);
    formData.append("quantity", quantity || 1);

    if (config.discount.enabled && config.discount.propertyKey) {
      formData.append(
        "properties[" + config.discount.propertyKey + "]",
        config.discount.propertyValue,
      );
    }

    return formData;
  }

  function addToCart(formData, button, form) {
    if (!window.cart || typeof window.cart.addToCart !== "function") {
      if (form) {
        form.submit();
      }
      return;
    }

    window.cart.button = button;
    window.cart.form = form;
    window.cart.addToCart(formData);
  }

  function getQuantityFromForm(form) {
    if (!form) {
      return 1;
    }

    const quantityInput = form.querySelector('[name="quantity"]');

    if (!quantityInput) {
      return 1;
    }

    const quantity = parseInt(quantityInput.value, 10);
    return isNaN(quantity) || quantity < 1 ? 1 : quantity;
  }

  function buildOriginalFormData(form) {
    return new FormData(form);
  }

  function bindModalEvents(modal) {
    if (modal.dataset.bound === "true") {
      return;
    }

    modal.dataset.bound = "true";

    modal.addEventListener("click", function (event) {
      if (activePopup !== modal) {
        return;
      }

      const closeTarget = event.target.closest("[data-bi-upsell-close]");
      const acceptTarget = event.target.closest("[data-bi-upsell-accept]");
      const declineTarget = event.target.closest("[data-bi-upsell-decline]");

      if (closeTarget || declineTarget) {
        event.preventDefault();

        if (isEditorPreview(modal)) {
          closeEditorPreview(modal);
          return;
        }

        if (!activeContext) {
          return;
        }

        handleDecline(activeContext, declineTarget ? "decline" : "close");
        return;
      }

      if (acceptTarget) {
        event.preventDefault();

        if (isEditorPreview(modal)) {
          return;
        }

        handleAccept(activeContext);
      }
    });
  }

  function handleAccept(context) {
    const config = context.config;
    const modal = context.modal;
    const quantity = getQuantityFromForm(context.form);
    const formData = buildAdvancedFormData(config, quantity);

    trackUpsellEvent("bi_upsell_accept", context, "accept");
    markPopupShown(config);
    closePopup(modal, context.button);
    addToCart(formData, context.button, context.form);
    cartCache = null;
  }

  function handleDecline(context, action) {
    const config = context.config;
    const modal = context.modal;
    const formData = buildOriginalFormData(context.form);
    const declineAction = action || "decline";

    trackUpsellEvent(
      declineAction === "close" ? "bi_upsell_close" : "bi_upsell_decline",
      context,
      declineAction,
    );
    markPopupShown(config);
    closePopup(modal, context.button);
    addToCart(formData, context.button, context.form);
    cartCache = null;
  }

  function shouldSkipButton(button, form) {
    if (!button || button.disabled) {
      return true;
    }

    if (button.closest(SELECTORS.quickViewOnboarding)) {
      return true;
    }

    if (form && form.querySelector('[type="file"]')) {
      return true;
    }

    return false;
  }

  async function handleAtcClick(event) {
    const clickedElement = event.target;
    const button = clickedElement.matches(SELECTORS.atcButton)
      ? clickedElement
      : clickedElement.closest(SELECTORS.atcButton);

    if (!button) {
      return;
    }

    const form = button.closest("form");

    if (shouldSkipButton(button, form)) {
      return;
    }

    const configs = getConfigs();

    if (!configs.length) {
      return;
    }

    const handle = resolveProductHandle(button, form);

    if (!handle) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const product = await fetchProduct(handle);

    if (!product) {
      addToCart(buildOriginalFormData(form), button, form);
      return;
    }

    const config = findMatchingConfig(configs, product);

    if (!config) {
      addToCart(buildOriginalFormData(form), button, form);
      return;
    }

    if (shouldSuppressPopup(config)) {
      addToCart(buildOriginalFormData(form), button, form);
      return;
    }

    const cart = await fetchCart(false);

    if (cartHasAdvancedProduct(cart, config.advanced.productId)) {
      addToCart(buildOriginalFormData(form), button, form);
      return;
    }

    const variantId = getVariantIdFromForm(form);
    const clickedPrice = getVariantPrice(product, variantId);
    const advancedPrice = config.advanced.price;
    const priceDiff = getDisplayPriceDiff(advancedPrice, clickedPrice, config);

    const modal = document.querySelector(
      '[data-bi-upsell-modal="' + config.id + '"]',
    );

    if (!modal) {
      addToCart(buildOriginalFormData(form), button, form);
      return;
    }

    bindModalEvents(modal);
    updatePopupContent(modal, config, priceDiff);

    activeContext = {
      config: config,
      modal: modal,
      button: button,
      form: form,
      priceDiff: priceDiff,
      basePriceDiff: Math.max(0, advancedPrice - clickedPrice),
      triggerHandle: product.handle || handle,
      triggerProductId: product.id || "",
    };

    openPopup(modal);
    trackUpsellEvent("bi_upsell_view", activeContext, "view");
  }

  function init() {
    const configs = getConfigs();

    if (!configs.length) {
      return;
    }

    for (const config of configs) {
      const modal = document.querySelector(
        '[data-bi-upsell-modal="' + config.id + '"]',
      );
      if (modal) {
        bindModalEvents(modal);
      }
    }

    document.addEventListener("click", handleAtcClick, true);

    document.addEventListener("theme:cart:added", function () {
      cartCache = null;
    });

    document.addEventListener("theme:cart:update", function () {
      cartCache = null;
    });

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && activePopup) {
        if (isEditorPreview(activePopup)) {
          closeEditorPreview(activePopup);
          return;
        }

        if (activeContext) {
          handleDecline(activeContext, "close");
        }
      }
    });

    initThemeEditor();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
